import * as Page from '../../components/content/page-header';
import { Content } from '../../components/layout/content';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { PaginatedTable } from '../../components/table/paginated-table';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { Pageable, Query, usePagination } from '../../hooks/use-pagination';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { Capability } from './types/capability';
import { ActionColumn } from '../../components/table/columns';

export const Capabilities = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const [query, setQuery] = useState<Query>({});
  const capabilities = usePagination<Pageable<Capability>>('/capabilities', query);
  const postMutation = usePostMutation<Capability>({ path: '/capabilities', cacheKey: '/capabilities' });
  const putMutation = usePutMutation<Capability>({ path: `/capabilities/${pageState.item?.id as string}`, cacheKey: '/capabilities' });
  const deleteMutation = useDeleteMutation<Capability>({ cacheKey: '/capabilities' });

  const onSubmit = (data: FieldValues): void => {
    !data.id ? postMutation.mutate(data) : putMutation.mutate(data);
    setPageState(Modes.BROWSE);
  };

  const addCapabilities = (): void => {
    reset();
    setPageState(Modes.CREATE);
  };

  const onDeleteConfirm = (): void => {
    setPageState(Modes.BROWSE);
    deleteMutation.mutate({ path: `/capabilities/${pageState.item?.id as string}` });
  };

  const onUpdate = (item: Capability): void => {
    for (const key in item) {
      setValue(key, item[key as keyof Capability]);
    }
    setPageState(Modes.UPDATE, item);
  };

  const columns = [
    { accessor: 'name', Header: 'Name', sort: true },
    { accessor: 'url', Header: 'Url', sort: true },
    { accessor: 'roleName', Header: 'Role', sort: true },
    ActionColumn({
      onDelete: (item: Capability): void => setPageState(Modes.DELETE, item),
      onUpdate
    })
  ];

  return <>
    <Content>
      <Page.Header>
        <Page.Title>Capabilities</Page.Title>
        <Page.SubTitle>
          Capabilities provide the connection to services used within RAPIDS.
          <div>
            <Button.LightBlackSmall onClick={addCapabilities}>
              <Icons.Add />
              Add Capability
            </Button.LightBlackSmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      <PaginatedTable isLoading={capabilities.isLoading} columns={columns} data={capabilities.data} onChange={setQuery} />
    </Content>
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>{pageState.mode === Modes.CREATE ? 'Add Capability' : 'Edit Capability'}</Title>
          <DialogContent>
            <div className="mb-4">
              <Label name="name">Capability Name*</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Capability Name' })} errors={errors} placeholder="Name of the capability" />
            </div>
            <div className="mb-4">
              <Label name="description">Description</Label>
              <TextInputLarge {...register('description')} placeholder="Description of the capability" />
            </div>
            <div className="mb-4">
              <Label name="url">Url</Label>
              <TextInputLarge {...register('url')} placeholder="Url of the capability" />
            </div>
            {/* <div className="mb-4">*/}
            {/*  <Label>Role</Label>*/}
            {/*  <Select2 { ...register('roleName')}>*/}
            {/*    <option value="">Select Role</option>*/}
            {/*  </Select2>*/}
            {/* </div>*/}
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Capability</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
